import type { AppRole, AustralianState, JobTitle } from '@empower-safety/shared-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface UpdateUserInput {
  user_id: string;
  display_name?: string;
  phone_e164?: string | null;
  state?: AustralianState | null;
  job_title?: JobTitle | null;
  // Modular capability toggles. admin is intentionally omitted (managed via
  // Clerk org_role). app_role is preserved for any caller still on the
  // legacy single-enum API; new callers pass is_worker/is_monitor.
  app_role?: AppRole;
  is_worker?: boolean;
  is_monitor?: boolean;
  team_ids?: string[];
}

export interface UpdatedUserDto {
  user_id: string;
  display_name: string;
  email: string;
  phone_e164: string | null;
  state: AustralianState | null;
  job_title: JobTitle | null;
  app_role: AppRole;
  is_worker: boolean;
  is_monitor: boolean;
  is_admin: boolean;
  team_ids: string[];
}

export function useUpdateUser() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ user_id, ...patch }: UpdateUserInput) =>
      api<{ user: UpdatedUserDto }>(`/users/${user_id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roster'] });
      qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}
